import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { database } from '../firebase';
import { ref, onValue } from 'firebase/database';
import AdminPasswordGate from '../components/AdminPasswordGate';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  color: #333;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  color: #666;
  text-transform: uppercase;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 2rem;
  border-bottom: 2px solid #ddd;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  background-color: ${props => props.active ? '#333' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid ${props => props.active ? '#333' : 'transparent'};
  margin-bottom: -2px;

  &:hover {
    background-color: ${props => props.active ? '#333' : '#f0f0f0'};
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-family: 'Cormorant Garamond', serif;
  color: #333;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #666;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const DownloadButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #333;
  color: white;
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.85rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border-radius: 4px;

  &:hover {
    background-color: #555;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.active ? '#333' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border: 1px solid #ddd;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.8rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;

  &:hover {
    background-color: ${props => props.active ? '#555' : '#f5f5f5'};
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
`;

const Card = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => props.color || '#333'};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Name = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  color: #333;
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 500;
  background-color: ${props => props.status === 'attending' ? '#e8f5e9' : props.status === 'partial' ? '#fff3e0' : '#ffebee'};
  color: ${props => props.status === 'attending' ? '#2e7d32' : props.status === 'partial' ? '#e65100' : '#c62828'};
`;

const GuestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const GuestStatus = styled.div`
  font-size: 0.95rem;
  color: ${props => props.attending ? '#2e7d32' : '#c62828'};
`;

const Detail = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  font-size: 0.95rem;
  color: #666;
  white-space: pre-wrap;
`;

const Timestamp = styled.div`
  font-size: 0.7rem;
  color: #999;
  margin-top: 1rem;
  font-style: italic;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #999;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
`;

function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('rsvps');
  const [rsvps, setRsvps] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const verified = localStorage.getItem('adminPasswordVerified');
    if (verified === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const rsvpRef = ref(database, 'rsvps');
    const addressRef = ref(database, 'addresses');

    const unsubscribeRsvps = onValue(rsvpRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const rsvpArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        rsvpArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // If the same party responded more than once, keep only the most
        // recent response. Parties are matched by their guest names (falling
        // back to the free-text name for older submissions).
        const partyKey = (rsvp) =>
          Array.isArray(rsvp.guests) && rsvp.guests.length > 0
            ? rsvp.guests.map(g => (g.name || '').trim().toLowerCase()).sort().join('|')
            : (rsvp.name || '').trim().toLowerCase();
        const latestByParty = new Map();
        const deduped = [];
        rsvpArray.forEach(rsvp => {
          const key = partyKey(rsvp);
          if (latestByParty.has(key)) {
            latestByParty.get(key).supersededCount += 1;
          } else {
            const entry = { ...rsvp, supersededCount: 0 };
            latestByParty.set(key, entry);
            deduped.push(entry);
          }
        });
        setRsvps(deduped);
      } else {
        setRsvps([]);
      }
    });

    const unsubscribeAddresses = onValue(addressRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const addressArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        addressArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setAddresses(addressArray);
      } else {
        setAddresses([]);
      }
      setLoading(false);
    });

    return () => {
      unsubscribeRsvps();
      unsubscribeAddresses();
    };
  }, [isAuthenticated]);

  // Newer RSVPs carry a per-guest `guests` array; older ones only have party-level fields.
  const getGuests = (rsvp) => (Array.isArray(rsvp.guests) && rsvp.guests.length > 0 ? rsvp.guests : null);

  const getRsvpStatus = (rsvp) => {
    const guests = getGuests(rsvp);
    if (guests) {
      const attending = guests.filter(g => g.attendance === 'attending').length;
      if (attending === 0) return 'declining';
      if (attending === guests.length) return 'attending';
      return 'partial';
    }
    return rsvp.attendance === 'attending' ? 'attending' : 'declining';
  };

  const statusLabel = (rsvp) => {
    const status = getRsvpStatus(rsvp);
    if (status === 'partial') {
      const guests = getGuests(rsvp);
      return `${guests.filter(g => g.attendance === 'attending').length} of ${guests.length} Attending`;
    }
    return status === 'attending' ? 'Attending' : 'Declining';
  };

  const filteredRsvps = rsvps.filter(rsvp => {
    if (filter === 'all') return true;
    return rsvp.attendance === filter;
  });

  // Count individual people (not parties). Newer RSVPs list each guest's
  // response; older ones only carry a party-level count for attendees.
  const countPeople = (rsvp) => {
    const guests = getGuests(rsvp);
    if (guests) {
      return {
        attending: guests.filter(g => g.attendance === 'attending').length,
        declining: guests.filter(g => g.attendance !== 'attending').length
      };
    }
    if (rsvp.attendance === 'attending') {
      return { attending: parseInt(rsvp.numberOfGuests) || 1, declining: 0 };
    }
    return { attending: 0, declining: 1 };
  };

  const rsvpStats = {
    total: rsvps.length,
    attending: rsvps.reduce((sum, r) => sum + countPeople(r).attending, 0),
    declining: rsvps.reduce((sum, r) => sum + countPeople(r).declining, 0)
  };

  const addressStats = {
    total: addresses.length
  };

  const downloadRsvpCSV = () => {
    const headers = ['Name', 'Attendance', 'Number of Guests', 'Attending Guests', 'Not Coming', 'Dietary Restrictions', 'Message', 'Timestamp'];
    const csvRows = [headers.join(',')];

    rsvps.forEach(rsvp => {
      const guests = getGuests(rsvp);
      const attendingNames = guests ? guests.filter(g => g.attendance === 'attending').map(g => g.name).join('; ') : '';
      const decliningNames = guests ? guests.filter(g => g.attendance !== 'attending').map(g => g.name).join('; ') : '';
      const row = [
        `"${rsvp.name || ''}"`,
        `"${statusLabel(rsvp)}"`,
        `"${rsvp.numberOfGuests || ''}"`,
        `"${attendingNames.replace(/"/g, '""')}"`,
        `"${decliningNames.replace(/"/g, '""')}"`,
        `"${(rsvp.dietaryRestrictions || '').replace(/"/g, '""')}"`,
        `"${(rsvp.message || '').replace(/"/g, '""')}"`,
        `"${rsvp.timestamp || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wedding-rsvps-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadAddressCSV = () => {
    const headers = ['Mailing Name', 'Street Address', 'Address Line 2', 'City', 'State', 'ZIP Code', 'Timestamp'];
    const csvRows = [headers.join(',')];

    addresses.forEach(address => {
      const row = [
        `"${(address.name || '').replace(/"/g, '""')}"`,
        `"${(address.street || '').replace(/"/g, '""')}"`,
        `"${(address.street2 || '').replace(/"/g, '""')}"`,
        `"${(address.city || '').replace(/"/g, '""')}"`,
        `"${(address.state || '').replace(/"/g, '""')}"`,
        `"${(address.zip || '').replace(/"/g, '""')}"`,
        `"${address.timestamp || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wedding-addresses-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isAuthenticated) {
    return <AdminPasswordGate onCorrectPassword={() => setIsAuthenticated(true)} />;
  }

  if (loading) {
    return (
      <AppContainer>
        <Container>
          <LoadingState>Loading...</LoadingState>
        </Container>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Container>
        <Header>
          <Title>Admin Dashboard</Title>
          <Subtitle>HANA & DAVID'S WEDDING</Subtitle>
        </Header>

        <TabContainer>
          <Tab
            active={activeTab === 'rsvps'}
            onClick={() => setActiveTab('rsvps')}
          >
            RSVPs
          </Tab>
          <Tab
            active={activeTab === 'addresses'}
            onClick={() => setActiveTab('addresses')}
          >
            Addresses
          </Tab>
        </TabContainer>

        {activeTab === 'rsvps' && (
          <>
            <Stats>
              <StatCard>
                <StatNumber>{rsvpStats.total}</StatNumber>
                <StatLabel>Total Responses</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{rsvpStats.attending}</StatNumber>
                <StatLabel>Attending</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{rsvpStats.declining}</StatNumber>
                <StatLabel>Declining</StatLabel>
              </StatCard>
            </Stats>

            <Controls>
              <FilterGroup>
                <FilterButton
                  active={filter === 'all'}
                  onClick={() => setFilter('all')}
                >
                  All ({rsvpStats.total})
                </FilterButton>
                <FilterButton
                  active={filter === 'attending'}
                  onClick={() => setFilter('attending')}
                >
                  Attending ({rsvps.filter(r => r.attendance === 'attending').length})
                </FilterButton>
                <FilterButton
                  active={filter === 'declining'}
                  onClick={() => setFilter('declining')}
                >
                  Declining ({rsvps.filter(r => r.attendance === 'declining').length})
                </FilterButton>
              </FilterGroup>
              <DownloadButton onClick={downloadRsvpCSV} disabled={rsvps.length === 0}>
                Download CSV
              </DownloadButton>
            </Controls>

            {filteredRsvps.length === 0 ? (
              <EmptyState>
                {rsvps.length === 0
                  ? 'No RSVPs yet. They will appear here once guests start responding.'
                  : `No ${filter === 'attending' ? 'attending' : 'declining'} responses.`}
              </EmptyState>
            ) : (
              <Grid>
                {filteredRsvps.map(rsvp => {
                  const status = getRsvpStatus(rsvp);
                  const guests = getGuests(rsvp);
                  return (
                  <Card key={rsvp.id} color={status === 'attending' ? '#4CAF50' : status === 'partial' ? '#FF9800' : '#f44336'}>
                    <CardHeader>
                      <Name>{rsvp.name}</Name>
                      <Badge status={status}>
                        {statusLabel(rsvp)}
                      </Badge>
                    </CardHeader>

                    {guests && (
                      <Detail>
                        <DetailLabel>Guest Responses</DetailLabel>
                        <GuestList>
                          {guests.map(guest => (
                            <GuestStatus key={guest.name} attending={guest.attendance === 'attending'}>
                              {guest.attendance === 'attending' ? '✓' : '✗'} {guest.name} — {guest.attendance === 'attending' ? 'Attending' : 'Not coming'}
                            </GuestStatus>
                          ))}
                        </GuestList>
                      </Detail>
                    )}

                    {!guests && rsvp.numberOfGuests && rsvp.attendance === 'attending' && (
                      <Detail>
                        <DetailLabel>Number of Guests (including them)</DetailLabel>
                        <DetailValue>{rsvp.numberOfGuests}</DetailValue>
                      </Detail>
                    )}

                    {rsvp.dietaryRestrictions && (
                      <Detail>
                        <DetailLabel>Dietary Restrictions</DetailLabel>
                        <DetailValue>{rsvp.dietaryRestrictions}</DetailValue>
                      </Detail>
                    )}

                    {rsvp.message && (
                      <Detail>
                        <DetailLabel>Message</DetailLabel>
                        <DetailValue>{rsvp.message}</DetailValue>
                      </Detail>
                    )}

                    <Timestamp>
                      Submitted {formatTimestamp(rsvp.timestamp)}
                      {rsvp.supersededCount > 0 && ` • Updated response (replaces ${rsvp.supersededCount} earlier ${rsvp.supersededCount === 1 ? 'submission' : 'submissions'})`}
                    </Timestamp>
                  </Card>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        {activeTab === 'addresses' && (
          <>
            <Stats>
              <StatCard>
                <StatNumber>{addressStats.total}</StatNumber>
                <StatLabel>Total Addresses</StatLabel>
              </StatCard>
            </Stats>

            <Controls>
              <div></div>
              <DownloadButton onClick={downloadAddressCSV} disabled={addresses.length === 0}>
                Download CSV
              </DownloadButton>
            </Controls>

            {addresses.length === 0 ? (
              <EmptyState>
                No addresses yet. They will appear here once guests submit their mailing information.
              </EmptyState>
            ) : (
              <Grid>
                {addresses.map(address => (
                  <Card key={address.id} color="#333">
                    <CardHeader>
                      <Name>{address.name}</Name>
                    </CardHeader>

                    <Detail>
                      <DetailLabel>Mailing Address</DetailLabel>
                      <DetailValue>
                        {address.street}
                        {address.street2 && `\n${address.street2}`}
                        {'\n'}
                        {address.city}, {address.state} {address.zip}
                      </DetailValue>
                    </Detail>

                    <Timestamp>
                      Submitted {formatTimestamp(address.timestamp)}
                    </Timestamp>
                  </Card>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </AppContainer>
  );
}

export default AdminPage;
